import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Bell,
  CheckCircle2,
  XCircle,
  Clock,
  Check,
  Activity,
  Eye,
  Zap,
  Server,
  Search,
  X,
  ChevronRight,
  ShieldQuestionMark,
} from 'lucide-react';

export interface ClientStatItem {
  client_id?: number | string;
  client_name?: string;
  name?: string;
  fire_count?: number;
  total_fire_count?: number;
  total_fired?: number;
  open_count?: number;
  open?: number;
  acknowledged_count?: number;
  acknowledged?: number;
  ack_count?: number;
  resolved_count?: number;
  resolved?: number;
  last_fired_at?: string | null;
  last_fired?: string | null;
  success?: number;
  success_count?: number;
  failed?: number;
  failed_count?: number;
}

export interface TriggerNodeStats {
  total_fire_count?: number;
  total_fired?: number;
  fire_count?: number;
  fired_count?: number;
  open_count?: number;
  open?: number;
  acknowledged_count?: number;
  acknowledged?: number;
  ack_count?: number;
  resolved_count?: number;
  resolved?: number;
  last_fired_at?: string | null;
  last_fired?: string | null;
  by_client?: ClientStatItem[];
}

export interface TriggerNodeStatsProps {
  stats?: TriggerNodeStats;
  compact?: boolean;
}

export interface ActionNodeStats {
  success?: number;
  success_count?: number;
  successful?: number;
  failed?: number;
  failed_count?: number;
  failure?: number;
  total_executions?: number;
  last_executed_at?: string | null;
  last_run_at?: string | null;
  avg_duration_ms?: number;
  by_client?: ClientStatItem[];
}

export interface ActionNodeStatsProps {
  stats?: ActionNodeStats;
  compact?: boolean;
}

// Helper to format timestamps gracefully into human-readable text
const formatTimeAgo = (dateStr?: string | null): string | null => {
  if (!dateStr) return null;
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return null;

    const now = new Date();
    const diffSec = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffSec < 0 || diffSec < 60) return 'Just now';
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
    return `${Math.floor(diffSec / 86400)}d ago`;
  } catch {
    return null;
  }
};

// Client Stats Sidebar Drawer Portal Component
const ClientStatsSidebar: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  title: string;
  nodeType: 'trigger' | 'action';
  clients: ClientStatItem[];
  totalFired?: number;
  totalOpen?: number;
  totalResolved?: number;
  totalSuccess?: number;
  totalFailed?: number;
  lastActive?: string | null;
}> = ({
  isOpen,
  onClose,
  title,
  nodeType,
  clients,
  totalFired = 0,
  totalOpen = 0,
  totalResolved = 0,
  totalSuccess = 0,
  totalFailed = 0,
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [activeFilter] = useState<'all' | 'fired' | 'open' | 'failed'>('all');

    const handleClose = (e?: React.MouseEvent) => {
      if (e) {
        e.stopPropagation();
        e.preventDefault();
      }
      window.dispatchEvent(new CustomEvent('closeAutomationConfigSidebar'));
      onClose();
    };

    useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') handleClose();
      };
      if (isOpen) {
        window.addEventListener('keydown', handleKeyDown);
      }
      return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const filteredClients = clients.filter((client) => {
      const cName = (client.client_name || client.name || `Client #${client.client_id || ''}`).toLowerCase();
      const matchesSearch = !searchTerm.trim() || cName.includes(searchTerm.toLowerCase());

      if (!matchesSearch) return false;

      const cFired = client.fire_count ?? client.total_fire_count ?? client.total_fired ?? 0;
      const cOpen = client.open_count ?? client.open ?? 0;
      const cFailed = client.failed ?? client.failed_count ?? 0;

      if (activeFilter === 'fired') return cFired > 0;
      if (activeFilter === 'open') return cOpen > 0;
      if (activeFilter === 'failed') return cFailed > 0;

      return true;
    });

    const sidebarContent = (
      <div className="fixed inset-0 z-[99999] flex justify-end select-none pointer-events-auto">
        {/* Dimmed Backdrop */}
        <div
          className="fixed inset-0 bg-slate-950/70 backdrop-blur-sm transition-opacity animate-in fade-in duration-200"
          onClick={handleClose}
          onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
          onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
        />

        {/* Slide-Over Right Sidebar */}
        <div
          className="relative w-full max-w-md bg-white dark:bg-slate-950 border-l border-gray-200 dark:border-slate-800 shadow-2xl flex flex-col h-full z-10 animate-in slide-in-from-right duration-300 font-sans text-gray-900 dark:text-slate-100"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {/* Compact Header Bar */}
          <div className="px-4 py-3 border-b border-gray-200 dark:border-slate-800 bg-gray-50/80 dark:bg-slate-900/80 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center text-cyan-600 dark:text-cyan-400">
                <Server className="w-4 h-4" />
              </div>
              <div>
                <h3 className="text-xs font-bold text-gray-900 dark:text-slate-100 flex items-center gap-1.5 leading-tight">
                  <span>{title}</span>
                  <span className="text-[9px] bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border border-cyan-500/30 px-1.5 py-0.2 rounded font-mono font-bold">
                    {clients.length}
                  </span>
                </h3>
                <p className="text-[10px] text-gray-500 dark:text-slate-400 leading-none mt-0.5">Per-client execution & alert metrics</p>
              </div>
            </div>

            <button
              onClick={handleClose}
              onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
              onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
              className="w-6 h-6 rounded-md bg-gray-100 dark:bg-slate-900 border border-gray-200 dark:border-slate-800 text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-slate-800 flex items-center justify-center transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Compact Summary Strip */}
          <div className="px-3 py-2 border-b border-gray-200 dark:border-slate-800/80 bg-gray-50/40 dark:bg-slate-900/40 flex items-center justify-between text-[10px]">
            {nodeType === 'trigger' ? (
              <div className="flex items-center gap-3 w-full justify-between px-1">
                <div className="flex items-center gap-1 text-amber-600 dark:text-amber-400 font-bold">
                  <ShieldQuestionMark className="w-3 h-3 text-amber-500" />
                  <span>Fired: <strong className="text-gray-900 dark:text-slate-100">{totalFired}</strong></span>
                </div>
                <div className="flex items-center gap-1 text-red-600 dark:text-red-400 font-bold">
                  <Bell className="w-3 h-3 text-red-500 dark:text-red-400" />
                  <span>Open: <strong className="text-gray-900 dark:text-slate-100">{totalOpen}</strong></span>
                </div>
                <div className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-bold">
                  <CheckCircle2 className="w-3 h-3 text-emerald-500 dark:text-emerald-400" />
                  <span>Resolved: <strong className="text-gray-900 dark:text-slate-100">{totalResolved}</strong></span>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3 w-full justify-between px-1">
                <div className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-bold">
                  <Check className="w-3 h-3 text-emerald-500 dark:text-emerald-400 stroke-[3]" />
                  <span>Success: <strong className="text-gray-900 dark:text-slate-100">{totalSuccess}</strong></span>
                </div>
                <div className="flex items-center gap-1 text-red-600 dark:text-red-400 font-bold">
                  <XCircle className="w-3 h-3 text-red-500 dark:text-red-400" />
                  <span>Failed: <strong className="text-gray-900 dark:text-slate-100">{totalFailed}</strong></span>
                </div>
                <div className="flex items-center gap-1 text-cyan-600 dark:text-cyan-400 font-bold">
                  <Server className="w-3 h-3 text-cyan-500 dark:text-cyan-400" />
                  <span>Clients: <strong className="text-gray-900 dark:text-slate-100">{clients.length}</strong></span>
                </div>
              </div>
            )}
          </div>

          {/* Compact Search & Filter Controls */}
          <div className="px-2 pt-2">
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-gray-400 dark:text-slate-500 absolute left-2.5 top-2" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Filter clients by name or ID..."
                className="w-full bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-md pl-8 pr-3 py-1 text-[11px] text-gray-900 dark:text-slate-200 placeholder:text-gray-400 dark:placeholder:text-slate-600 focus:outline-none focus:border-cyan-500/60 transition-colors"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-2 top-1.5 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>

          {/* High-Density Compact Client List Rows */}
          <div className="flex-1 overflow-y-auto p-2 space-y-1.5 custom-scrollbar">
            {filteredClients.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-gray-400 dark:text-slate-500 gap-1.5">
                <Server className="w-6 h-6 text-gray-300 dark:text-slate-700 stroke-1" />
                <span className="text-[11px]">No client matches query</span>
              </div>
            ) : (
              filteredClients.map((client, idx) => {
                const cName = client.client_name || client.name || `Client #${client.client_id ?? idx + 1}`;
                const cFired = client.fire_count ?? client.total_fire_count ?? client.total_fired ?? 0;
                const cOpen = client.open_count ?? client.open ?? 0;
                const cAck = client.acknowledged_count ?? client.acknowledged ?? 0;
                const cRes = client.resolved_count ?? client.resolved ?? 0;
                const cSuccess = client.success ?? client.success_count ?? 0;
                const cFailed = client.failed ?? client.failed_count ?? 0;
                const cTotalExec = cSuccess + cFailed;
                const cRate = cTotalExec > 0 ? Math.round((cSuccess / cTotalExec) * 100) : 100;
                const cLastFired = client.last_fired_at || client.last_fired;
                const cTimeAgo = formatTimeAgo(cLastFired);

                return (
                  <div
                    key={client.client_id || idx}
                    className="bg-gray-50/80 dark:bg-slate-900/80 border border-gray-200 dark:border-slate-800/90 hover:border-cyan-500/40 rounded-lg p-2 flex items-center justify-between transition-all duration-150 group"
                  >
                    {/* Left: Client Name & ID */}
                    <div className="flex items-center gap-2 min-w-0 pr-2">
                      <div className="flex flex-col min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="font-bold text-[11px] text-gray-900 dark:text-slate-100 truncate group-hover:text-cyan-600 dark:group-hover:text-cyan-300 transition-colors" title={cName}>
                            {cName}
                          </span>
                          {client.client_id !== undefined && client.client_id !== null && (
                            <span className="text-[8px] font-mono text-gray-500 dark:text-slate-400 bg-gray-100 dark:bg-slate-950 px-1 py-0.2 rounded border border-gray-200 dark:border-slate-800 shrink-0">
                              #{client.client_id}
                            </span>
                          )}
                        </div>
                        {cTimeAgo && (
                          <span className="text-[9px] text-gray-500 dark:text-slate-500 flex items-center gap-0.5 font-mono">
                            <Clock className="w-2 h-2 text-gray-400 dark:text-slate-500 shrink-0" />
                            {cTimeAgo}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Right: Inline Metric Pill Bar */}
                    <div className="flex items-center gap-1 shrink-0 text-[10px]">
                      {nodeType === 'trigger' ? (
                        <>
                          <span className="px-1.5 py-0.5 rounded bg-amber-500/10 border border-amber-500/20 text-amber-600 dark:text-amber-400 font-bold flex items-center gap-0.5" title="Fired">
                            <ShieldQuestionMark className="w-2.5 h-2.5 text-amber-500 shrink-0" />
                            {cFired}
                          </span>

                          <span className={`px-1.5 py-0.5 rounded border font-bold flex items-center gap-0.5 ${cOpen > 0 ? 'bg-red-500/10 dark:bg-red-500/20 border-red-500/30 dark:border-red-500/40 text-red-600 dark:text-red-400 animate-pulse' : 'bg-gray-100 dark:bg-slate-950/60 border-gray-200 dark:border-slate-800 text-gray-400 dark:text-slate-500'}`} title="Open Alerts">
                            <Bell className="w-2.5 h-2.5 shrink-0" />
                            {cOpen}
                          </span>

                          {cAck > 0 && (
                            <span className="px-1.5 py-0.5 rounded bg-amber-500/10 border border-amber-500/20 text-amber-600 dark:text-amber-300 font-bold flex items-center gap-0.5" title="Acknowledged">
                              <Eye className="w-2.5 h-2.5 shrink-0" />
                              {cAck}
                            </span>
                          )}

                          <span className="px-1.5 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 font-bold flex items-center gap-0.5" title="Resolved">
                            <CheckCircle2 className="w-2.5 h-2.5 text-emerald-500 dark:text-emerald-400 shrink-0" />
                            {cRes}
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="px-1.5 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 font-bold flex items-center gap-0.5" title="Success">
                            <Check className="w-2.5 h-2.5 text-emerald-500 dark:text-emerald-400 stroke-[3] shrink-0" />
                            {cSuccess}
                          </span>

                          {cFailed > 0 && (
                            <span className="px-1.5 py-0.5 rounded bg-red-500/10 dark:bg-red-500/20 border-red-500/30 dark:border-red-500/40 text-red-600 dark:text-red-400 font-bold flex items-center gap-0.5" title="Failed">
                              <XCircle className="w-2.5 h-2.5 shrink-0" />
                              {cFailed}
                            </span>
                          )}

                          <span className={`px-1.5 py-0.5 rounded border text-[9px] font-mono font-bold ${cRate >= 90 ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20' : 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20'}`} title="Success Rate">
                            {cRate}%
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Compact Footer */}
          <div className="px-3 py-2 border-t border-gray-200 dark:border-slate-800 bg-gray-50/60 dark:bg-slate-900/60 text-center text-[10px] text-gray-500 dark:text-slate-500 flex items-center justify-between">
            <span>Showing {filteredClients.length} of {clients.length} client(s)</span>
            <span className="text-[9px] text-cyan-600 dark:text-cyan-400 font-mono">Live Sync Active</span>
          </div>
        </div>
      </div>
    );

    return createPortal(sidebarContent, document.body);
  };

export const TriggerNodeStatsBadges: React.FC<TriggerNodeStatsProps> = ({ stats }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  if (!stats) return null;

  const fireCount = stats.total_fire_count ?? stats.total_fired ?? stats.fire_count ?? stats.fired_count ?? 0;
  const openCount = stats.open_count ?? stats.open ?? 0;
  const ackCount = stats.acknowledged_count ?? stats.acknowledged ?? stats.ack_count ?? 0;
  const resCount = stats.resolved_count ?? stats.resolved ?? 0;
  const lastFired = stats.last_fired_at || stats.last_fired;
  const timeAgo = formatTimeAgo(lastFired);
  const clientsList = Array.isArray(stats.by_client) ? stats.by_client : [];

  const totalAlerts = openCount + ackCount + resCount;
  const resPercent = totalAlerts > 0 ? Math.round((resCount / totalAlerts) * 100) : (fireCount > 0 ? 100 : 0);

  if (fireCount === 0 && openCount === 0 && ackCount === 0 && resCount === 0 && clientsList.length === 0) {
    return null;
  }

  return (
    <>
      <div
        className="mt-1.5 flex flex-col items-center gap-1 select-none pointer-events-auto relative group"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        {/* Primary Glassmorphic Badge Bar */}
        <div className="flex items-center gap-1.5 bg-white/95 dark:bg-slate-950/95 border border-gray-200 dark:border-slate-800/90 rounded-full px-2.5 py-1 shadow-lg backdrop-blur-md hover:border-amber-500/50 transition-all duration-200 cursor-pointer">
          {/* Fire Count */}
          <div className="flex items-center gap-1 text-[10px] font-bold text-amber-600 dark:text-amber-400" title="Total Fired Count">
            <ShieldQuestionMark className="w-3 h-3 text-amber-500 shrink-0 animate-pulse" />
            <span>{fireCount}</span>
          </div>

          <span className="text-[9px] text-gray-300 dark:text-slate-700 font-bold">•</span>

          {/* Open Alerts */}
          <div className={`flex items-center gap-1 text-[10px] font-bold ${openCount > 0 ? 'text-red-500 dark:text-red-400' : 'text-gray-400 dark:text-slate-400'}`} title="Open Alerts">
            <Bell className={`w-3 h-3 ${openCount > 0 ? 'text-red-500 animate-bounce' : 'text-gray-400 dark:text-slate-500'} shrink-0`} />
            <span>{openCount}</span>
          </div>

          {/* Acknowledged Count */}
          {ackCount > 0 && (
            <>
              <span className="text-[9px] text-gray-300 dark:text-slate-700 font-bold">•</span>
              <div className="flex items-center gap-1 text-[10px] font-bold text-amber-600 dark:text-amber-400" title="Acknowledged Alerts">
                <Eye className="w-3 h-3 text-amber-500 dark:text-amber-400 shrink-0" />
                <span>{ackCount}</span>
              </div>
            </>
          )}

          {/* Resolved Count */}
          {resCount > 0 && (
            <>
              <span className="text-[9px] text-gray-300 dark:text-slate-700 font-bold">•</span>
              <div className="flex items-center gap-1 text-[10px] font-bold text-emerald-600 dark:text-emerald-400" title="Resolved Alerts">
                <CheckCircle2 className="w-3 h-3 text-emerald-500 dark:text-emerald-400 shrink-0" />
                <span>{resCount}</span>
              </div>
            </>
          )}

          {/* Client Count Button Pill if Clients Exist */}
          {clientsList.length > 0 && (
            <>
              <span className="text-[9px] text-gray-300 dark:text-slate-700 font-bold">•</span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  window.dispatchEvent(new CustomEvent('closeAutomationConfigSidebar'));
                  setIsSidebarOpen(true);
                }}
                onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
                onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
                className="flex items-center gap-1 text-[10px] font-bold text-cyan-600 dark:text-cyan-400 hover:text-cyan-500 dark:hover:text-cyan-300 hover:underline transition-colors"
                title="Click to open Client Stats Sidebar"
              >
                <Server className="w-3 h-3 text-cyan-500 dark:text-cyan-400 shrink-0" />
                <span>{clientsList.length} Client{clientsList.length > 1 ? 's' : ''}</span>
              </button>
            </>
          )}

          {/* Time Ago pill */}
          {timeAgo && (
            <>
              <span className="text-[9px] text-gray-300 dark:text-slate-700 font-bold">•</span>
              <div className="flex items-center gap-0.5 text-[9px] font-medium text-gray-500 dark:text-slate-400" title={lastFired ? `Last Fired: ${lastFired}` : ''}>
                <Clock className="w-2.5 h-2.5 text-gray-400 dark:text-slate-400 shrink-0" />
                <span>{timeAgo}</span>
              </div>
            </>
          )}
        </div>

        {/* Compact Clean Hover Card */}
        {showTooltip && (
          <div className="absolute top-full mt-1.5 z-50 w-64 bg-white/98 dark:bg-slate-950/98 border border-gray-200 dark:border-slate-800 rounded-xl p-3 shadow-2xl backdrop-blur-xl text-gray-900 dark:text-slate-100 text-xs animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-slate-800 pb-1.5 mb-2">
              <div className="flex items-center gap-1.5 font-bold text-amber-600 dark:text-amber-400">
                <Zap className="w-3.5 h-3.5" />
                <span>Trigger Statistics</span>
              </div>
              <span className="text-[9px] bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20 px-1.5 py-0.5 rounded font-mono font-bold">
                {resPercent}% Resolved
              </span>
            </div>

            {/* Metric Overview Grid */}
            <div className="grid grid-cols-2 gap-1.5 mb-2">
              <div className="bg-gray-50 dark:bg-slate-900/80 border border-gray-100 dark:border-slate-800 rounded-lg p-1.5 flex flex-col">
                <span className="text-[9px] text-gray-500 dark:text-slate-400 flex items-center gap-1">
                  <ShieldQuestionMark className="w-2.5 h-2.5 text-amber-500" /> Total Fired
                </span>
                <span className="text-sm font-extrabold text-amber-600 dark:text-amber-400">{fireCount}</span>
              </div>

              <div className="bg-gray-50 dark:bg-slate-900/80 border border-gray-100 dark:border-slate-800 rounded-lg p-1.5 flex flex-col">
                <span className="text-[9px] text-gray-500 dark:text-slate-400 flex items-center gap-1">
                  <Bell className="w-2.5 h-2.5 text-red-500 dark:text-red-400" /> Open Alerts
                </span>
                <span className="text-sm font-extrabold text-red-500 dark:text-red-400">{openCount}</span>
              </div>

              <div className="bg-gray-50 dark:bg-slate-900/80 border border-gray-100 dark:border-slate-800 rounded-lg p-1.5 flex flex-col">
                <span className="text-[9px] text-gray-500 dark:text-slate-400 flex items-center gap-1">
                  <Eye className="w-2.5 h-2.5 text-amber-500 dark:text-amber-400" /> Acknowledged
                </span>
                <span className="text-sm font-extrabold text-amber-600 dark:text-amber-400">{ackCount}</span>
              </div>

              <div className="bg-gray-50 dark:bg-slate-900/80 border border-gray-100 dark:border-slate-800 rounded-lg p-1.5 flex flex-col">
                <span className="text-[9px] text-gray-500 dark:text-slate-400 flex items-center gap-1">
                  <CheckCircle2 className="w-2.5 h-2.5 text-emerald-500 dark:text-emerald-400" /> Resolved
                </span>
                <span className="text-sm font-extrabold text-emerald-600 dark:text-emerald-400">{resCount}</span>
              </div>
            </div>

            {/* Health Bar */}
            <div className="space-y-1 mb-2.5">
              <div className="flex justify-between text-[9px] text-gray-500 dark:text-slate-400 font-medium">
                <span>Resolution Health</span>
                <span>{resPercent}%</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-slate-900 rounded-full h-1.5 overflow-hidden flex">
                <div className="bg-emerald-500 h-full" style={{ width: `${resPercent}%` }} />
                <div className="bg-red-500 h-full" style={{ width: `${100 - resPercent}%` }} />
              </div>
            </div>

            {/* Client Sidebar Open CTA Button */}
            {clientsList.length > 0 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setShowTooltip(false);
                  window.dispatchEvent(new CustomEvent('closeAutomationConfigSidebar'));
                  setIsSidebarOpen(true);
                }}
                onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
                onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
                className="w-full bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-600 dark:text-cyan-400 border border-cyan-500/30 rounded-lg py-1.5 px-2 font-bold text-[10px] flex items-center justify-between transition-all"
              >
                <div className="flex items-center gap-1.5">
                  <Server className="w-3 h-3" />
                  <span>View Breakdown ({clientsList.length} Clients)</span>
                </div>
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            )}

            {lastFired && (
              <div className="mt-2 pt-1.5 border-t border-gray-100 dark:border-slate-800 text-[9px] text-gray-500 dark:text-slate-400 flex items-center justify-between">
                <span>Last Fired:</span>
                <span className="font-mono text-gray-700 dark:text-slate-300">{new Date(lastFired).toLocaleString()}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Render Client Sidebar Drawer */}
      <ClientStatsSidebar
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        title="Trigger Client Breakdown"
        nodeType="trigger"
        clients={clientsList}
        totalFired={fireCount}
        totalOpen={openCount}
        totalResolved={resCount}
        lastActive={lastFired}
      />
    </>
  );
};

export const ActionNodeStatsBadges: React.FC<ActionNodeStatsProps> = ({ stats }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  if (!stats) return null;

  const success = stats.success ?? stats.success_count ?? stats.successful ?? 0;
  const failed = stats.failed ?? stats.failed_count ?? stats.failure ?? 0;
  const total = stats.total_executions ?? (success + failed);
  const lastExecuted = stats.last_executed_at || stats.last_run_at;
  const timeAgo = formatTimeAgo(lastExecuted);
  const clientsList = Array.isArray(stats.by_client) ? stats.by_client : [];

  if (success === 0 && failed === 0 && total === 0 && clientsList.length === 0) return null;

  const successRate = total > 0 ? Math.round((success / total) * 100) : 100;

  return (
    <>
      <div
        className="mt-1.5 flex flex-col items-center gap-1 select-none pointer-events-auto relative group"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        {/* Primary Glassmorphic Badge Bar */}
        <div className="flex items-center gap-1.5 bg-white/95 dark:bg-slate-950/95 border border-gray-200 dark:border-slate-800/90 rounded-full px-2.5 py-1 shadow-lg backdrop-blur-md hover:border-emerald-500/50 transition-all duration-200 cursor-pointer">
          {/* Success Badge */}
          <div className="flex items-center gap-1 text-[10px] font-bold text-emerald-600 dark:text-emerald-400" title={`Successful Executions: ${success}`}>
            <Check className="w-3 h-3 text-emerald-500 dark:text-emerald-400 shrink-0 stroke-[3]" />
            <span>{success}</span>
          </div>

          {/* Failed Badge */}
          {failed > 0 && (
            <>
              <span className="text-[9px] text-gray-300 dark:text-slate-700 font-bold">•</span>
              <div className="flex items-center gap-1 text-[10px] font-bold text-red-500 dark:text-red-400" title={`Failed Executions: ${failed}`}>
                <XCircle className="w-3 h-3 text-red-500 dark:text-red-400 shrink-0" />
                <span>{failed}</span>
              </div>
            </>
          )}

          {/* Success Rate Badge */}
          <span className="text-[9px] text-gray-300 dark:text-slate-700 font-bold">•</span>
          <div
            className={`flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full border ${successRate >= 90
              ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30'
              : successRate >= 75
                ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30'
                : 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/30'
              }`}
            title="Execution Success Rate"
          >
            <span>{successRate}%</span>
          </div>

          {/* Client Count Pill */}
          {clientsList.length > 0 && (
            <>
              <span className="text-[9px] text-gray-300 dark:text-slate-700 font-bold">•</span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  window.dispatchEvent(new CustomEvent('closeAutomationConfigSidebar'));
                  setIsSidebarOpen(true);
                }}
                onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
                onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
                className="flex items-center gap-1 text-[10px] font-bold text-cyan-600 dark:text-cyan-400 hover:text-cyan-500 dark:hover:text-cyan-300 hover:underline transition-colors"
                title="Click to open Client Stats Sidebar"
              >
                <Server className="w-3 h-3 text-cyan-500 dark:text-cyan-400 shrink-0" />
                <span>{clientsList.length} Client{clientsList.length > 1 ? 's' : ''}</span>
              </button>
            </>
          )}

          {/* Time Ago pill */}
          {timeAgo && (
            <>
              <span className="text-[9px] text-gray-300 dark:text-slate-700 font-bold">•</span>
              <div className="flex items-center gap-0.5 text-[9px] font-medium text-gray-500 dark:text-slate-400" title={lastExecuted ? `Last Run: ${lastExecuted}` : ''}>
                <Clock className="w-2.5 h-2.5 text-gray-400 dark:text-slate-400 shrink-0" />
                <span>{timeAgo}</span>
              </div>
            </>
          )}
        </div>

        {/* Compact Clean Hover Card */}
        {showTooltip && (
          <div className="absolute top-full mt-1.5 z-50 w-64 bg-white/98 dark:bg-slate-950/98 border border-gray-200 dark:border-slate-800 rounded-xl p-3 shadow-2xl backdrop-blur-xl text-gray-900 dark:text-slate-100 text-xs animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-slate-800 pb-1.5 mb-2">
              <div className="flex items-center gap-1.5 font-bold text-teal-600 dark:text-teal-400">
                <Activity className="w-3.5 h-3.5" />
                <span>Action Execution Stats</span>
              </div>
              <span
                className={`text-[9px] px-1.5 py-0.5 rounded font-mono font-bold border ${successRate >= 90
                  ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20'
                  : 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20'
                  }`}
              >
                {successRate}% Success
              </span>
            </div>

            <div className="grid grid-cols-2 gap-1.5 mb-2">
              <div className="bg-gray-50 dark:bg-slate-900/80 border border-gray-100 dark:border-slate-800 rounded-lg p-1.5 flex flex-col">
                <span className="text-[9px] text-gray-500 dark:text-slate-400 flex items-center gap-1">
                  <Check className="w-2.5 h-2.5 text-emerald-500 dark:text-emerald-400 stroke-[3]" /> Successful
                </span>
                <span className="text-sm font-extrabold text-emerald-600 dark:text-emerald-400">{success}</span>
              </div>

              <div className="bg-gray-50 dark:bg-slate-900/80 border border-gray-100 dark:border-slate-800 rounded-lg p-1.5 flex flex-col">
                <span className="text-[9px] text-gray-500 dark:text-slate-400 flex items-center gap-1">
                  <XCircle className="w-2.5 h-2.5 text-red-500 dark:text-red-400" /> Failed
                </span>
                <span className="text-sm font-extrabold text-red-500 dark:text-red-400">{failed}</span>
              </div>
            </div>

            {/* Success Rate Bar */}
            <div className="space-y-1 mb-2.5">
              <div className="flex justify-between text-[9px] text-gray-500 dark:text-slate-400 font-medium">
                <span>Success Rate</span>
                <span>{successRate}%</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-slate-900 rounded-full h-1.5 overflow-hidden flex">
                <div className="bg-emerald-500 h-full" style={{ width: `${successRate}%` }} />
                <div className="bg-red-500 h-full" style={{ width: `${100 - successRate}%` }} />
              </div>
            </div>

            {/* Client Sidebar Open CTA Button */}
            {clientsList.length > 0 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setShowTooltip(false);
                  window.dispatchEvent(new CustomEvent('closeAutomationConfigSidebar'));
                  setIsSidebarOpen(true);
                }}
                onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
                onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
                className="w-full bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-600 dark:text-cyan-400 border border-cyan-500/30 rounded-lg py-1.5 px-2 font-bold text-[10px] flex items-center justify-between transition-all"
              >
                <div className="flex items-center gap-1.5">
                  <Server className="w-3 h-3" />
                  <span>View Breakdown ({clientsList.length} Clients)</span>
                </div>
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            )}

            {lastExecuted && (
              <div className="mt-2 pt-1.5 border-t border-gray-100 dark:border-slate-800 text-[9px] text-gray-500 dark:text-slate-400 flex items-center justify-between">
                <span>Last Run:</span>
                <span className="font-mono text-gray-700 dark:text-slate-300">{new Date(lastExecuted).toLocaleString()}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Render Client Sidebar Drawer */}
      <ClientStatsSidebar
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        title="Action Client Breakdown"
        nodeType="action"
        clients={clientsList}
        totalSuccess={success}
        totalFailed={failed}
        lastActive={lastExecuted}
      />
    </>
  );
};
